// src/components/RPA/buttons/ExtractControlObjectivesButton.js - Fixed with proper polling
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 3000;        // More frequent polling like SOC
const MAX_MINUTES = 20;      // Stop polling after this many minutes

const ExtractControlObjectivesButton = ({ docId, disabledHint, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | extracting | done | error
  const [queuedMsg, setQueuedMsg] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const pollCountRef = useRef(0);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    pollCountRef.current = 0;
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
    // Reset state when closing
    setPhase('idle');
    setResult(null);
    setErr('');
    setElapsedSec(0);
  };

  // Enhanced document fetching with cache busting (copied from SOC pattern)
  const fetchDocMeta = async () => {
    const timestamp = Date.now();
    pollCountRef.current += 1;
    
    console.log(`[OBJECTIVES] Poll #${pollCountRef.current} for docId: ${docId}`);
    
    try {
      // Force fresh data with multiple cache-busting techniques
      const res = await axios.get(`${api}/admin/rpa/docs`, {
        params: { 
          _t: timestamp,
          _r: Math.random(),
          docId: docId // Specific doc filter if supported
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-None-Match': '*' // Prevent 304 responses
        },
        withCredentials: true
      });
      
      console.log(`[OBJECTIVES] Poll response status: ${res.status}`);
      
      const docs = Array.isArray(res?.data?.items) ? res.data.items : 
                   Array.isArray(res?.data?.docs) ? res.data.docs : 
                   Array.isArray(res?.data) ? res.data : [];
      
      const doc = docs.find(d => String(d._id) === String(docId));
      
      if (doc) {
        console.log(`[OBJECTIVES] Found doc:`, {
          _id: doc._id,
          controlObjectives: !!doc.controlObjectives,
          controlObjectivesQueuedAt: doc.controlObjectivesQueuedAt,
          lastControlObjectivesExtractedAt: doc.lastControlObjectivesExtractedAt,
          workflowAssignment: doc.workflowAssignment ? {
            templateId: doc.workflowAssignment.templateId,
            steps: doc.workflowAssignment.steps?.map(s => ({ id: s.id, status: s.status }))
          } : null
        });
      } else {
        console.warn(`[OBJECTIVES] Document ${docId} not found in response`);
      }
      
      return doc || null;
    } catch (error) {
      console.error(`[OBJECTIVES] Poll failed:`, error);
      return null;
    }
  };

  // Alternative strategy: Direct endpoint check (copied from SOC pattern)
  const checkExtractionStatus = async () => {
    try {
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/extract/objectives`,
        {},
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      console.log(`[OBJECTIVES] Direct check status: ${res.status}`);
      
      // If we get 200, extraction is complete
      if (res.status === 200) {
        return { completed: true, result: res.data };
      }
      
      return { completed: false, result: null };
    } catch (error) {
      console.warn(`[OBJECTIVES] Direct check failed:`, error.message);
      return { completed: false, result: null };
    }
  };

  // Polling loop when queued (enhanced like SOC pattern)
  const beginPolling = () => {
    console.log('[OBJECTIVES] Starting enhanced polling for docId:', docId);
    pollCountRef.current = 0;
    
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller with dual strategy
    timerRef.current = setInterval(async () => {
      try {
        // Strategy 1: Check via document list
        const doc = await fetchDocMeta();
        
        if (doc) {
          // Primary completion check: controlObjectives exists AND not queued
          if (doc.controlObjectives && !doc.controlObjectivesQueuedAt) {
            console.log('[OBJECTIVES] ✅ Completion detected via document list');
            stopTimers();
            setPhase('done');
            setResult({
              success: true,
              analysis: { azure: true, objectives: doc.controlObjectives },
            });
            if (typeof onDone === 'function') onDone(); // refresh doc list
            return;
          }
          
          // Secondary completion check: workflow step completed
          if (doc.workflowAssignment && doc.workflowAssignment.steps) {
            const objectivesStep = doc.workflowAssignment.steps.find(s => s.id === 'extract_objectives' || s.id === 'extract_control_objectives');
            if (objectivesStep && objectivesStep.status === 'completed') {
              console.log('[OBJECTIVES] ✅ Completion detected via workflow step');
              
              // Try to get objectives
              const statusCheck = await checkExtractionStatus();
              if (statusCheck.completed) {
                stopTimers();
                setPhase('done');
                setResult(statusCheck.result);
                if (typeof onDone === 'function') onDone();
                return;
              }
            }
          }
          
          // Still working - update UI hints
          if (doc.controlObjectivesQueuedAt) {
            setPhase('extracting');
            setLastUpdatedAt(doc.updatedAt || doc.createdAt || null);
          }
        }
        
        // Strategy 2: Every 5th poll, try direct endpoint check
        if (pollCountRef.current % 5 === 0) {
          console.log('[OBJECTIVES] Trying direct endpoint check...');
          const statusCheck = await checkExtractionStatus();
          if (statusCheck.completed) {
            console.log('[OBJECTIVES] ✅ Completion detected via direct check');
            stopTimers();
            setPhase('done');
            setResult(statusCheck.result);
            if (typeof onDone === 'function') onDone();
            return;
          }
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          console.warn('[OBJECTIVES] Polling timeout reached');
          stopTimers();
          setPhase('error');
          setErr('Control objectives extraction timed out. The extraction may have completed - try refreshing the page or clicking Extract Objectives again.');
        }
      } catch (e) {
        console.warn('[OBJECTIVES] Polling tick failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off control objectives extraction
      console.log('[OBJECTIVES] Starting extraction for docId:', docId);
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/extract/objectives`,
        {},
        { withCredentials: true, validateStatus: () => true }
      );

      console.log('[OBJECTIVES] Extract response:', res.status, res.data);

      // 200 => extraction is already available (cached) or completed inline
      if (res.status === 200) {
        console.log('[OBJECTIVES] Extraction already complete, showing results immediately');
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        if (typeof onDone === 'function') onDone(); // refresh doc list
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        console.log('[OBJECTIVES] Extraction queued, starting polling');
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for control objectives extraction...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[OBJECTIVES] extract objectives unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Control objectives extraction failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[OBJECTIVES] extract objectives failed:', e);
      setErr('Control objectives extraction failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure AI responded
    if (result.analysis?.azure) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ AI-Powered Control Objectives Extraction
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Extraction (Fallback)
      </p>
    );
  };

  const renderControlObjectives = () => {
    if (!result?.analysis?.objectives) return null;

    let objectives;
    try {
      objectives = typeof result.analysis.objectives === 'string' 
        ? JSON.parse(result.analysis.objectives) 
        : result.analysis.objectives;
    } catch {
      return <p style={{ color: '#666' }}>Unable to parse control objectives</p>;
    }

    const controlObjs = objectives.control_objectives || [];
    const summary = objectives.summary || {};

    return (
      <div style={objectivesContainer}>
        {/* Summary Box */}
        <div style={summaryBox}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Extraction Summary
          </h4>
          <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <div><strong>Total Objectives:</strong> {summary.total_objectives || controlObjs.length}</div>
            <div><strong>Critical Objectives:</strong> {summary.critical_objectives || 0}</div>
            <div><strong>Testing Complexity:</strong> {summary.testing_complexity || 'Not specified'}</div>
            <div><strong>Estimated Testing Hours:</strong> {summary.estimated_testing_hours || 'Not estimated'}</div>
          </div>
        </div>

        {/* Control Objectives List */}
        {controlObjs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Control Objectives ({controlObjs.length})
            </h4>
            <div style={objectivesGrid}>
              {controlObjs.slice(0, 6).map((obj, idx) => (
                <div key={idx} style={objectiveCard}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                    {obj.objective_id || `Objective ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151', marginBottom: 4 }}>
                    {obj.objective_title}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                    {obj.description}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ ...badge, background: getRiskColor(obj.risk_level) }}>
                      {obj.risk_level || 'medium'}
                    </span>
                    <span style={{ ...badge, background: '#f3f4f6', color: '#6b7280' }}>
                      {obj.control_domain || 'general'}
                    </span>
                    <span style={{ ...badge, background: '#ecfdf5', color: '#065f46' }}>
                      {obj.testing_approach || 'inquiry'}
                    </span>
                  </div>
                </div>
              ))}
              {controlObjs.length > 6 && (
                <div style={{ ...objectiveCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    +{controlObjs.length - 6} more objectives...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {controlObjs.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: 12 }}>
            No control objectives were extracted. Please verify the SOC analysis results contain control information.
          </p>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued for Extraction</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Processing SOC findings... {formatElapsed(elapsedSec)} (Poll #{pollCountRef.current})
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'extracting') {
      return (
        <div style={statusBox}>
          <strong>Extracting Control Objectives...</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Analyzing SOC findings to identify testable control objectives with Azure AI
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Elapsed {formatElapsed(elapsedSec)} • Poll #{pollCountRef.current}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            Using dual-strategy polling to detect completion
          </div>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div style={{ ...statusBox, borderColor: '#f87171', background: '#fef2f2' }}>
          <p style={{ color: 'crimson', margin: 0 }}>{err || 'Something went wrong.'}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <button
        onClick={run}
        disabled={busy || !!disabledHint}
        title={disabledHint || 'Extract structured control objectives from SOC analysis'}
        style={{ 
          background: '#10b981', 
          color: 'white', 
          border: 'none', 
          padding: '4px 8px', 
          borderRadius: 4,
          fontSize: 12,
          cursor: disabledHint ? 'not-allowed' : 'pointer',
          opacity: disabledHint ? 0.6 : 1
        }}
      >
        {busy ? 'Extracting…' : 'Extract Objectives'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Control Objectives Extraction</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              {renderControlObjectives()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Objectives (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                  {typeof result.analysis.objectives === 'string' 
                    ? result.analysis.objectives 
                    : JSON.stringify(result.analysis.objectives, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- helpers & styles ---------------------------- */

function getRiskColor(risk) {
  switch (risk) {
    case 'critical': return '#fef2f2';
    case 'high': return '#fef3c7';
    case 'medium': return '#eff6ff';
    case 'low': return '#f0fdf4';
    default: return '#f3f4f6';
  }
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  maxWidth: 800,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const objectivesContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 8
};

const summaryBox = {
  background: '#f0f9ff',
  border: '1px solid #0ea5e9',
  padding: 10,
  borderRadius: 6
};

const objectivesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 8
};

const objectiveCard = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  padding: 10,
  borderRadius: 6,
  minHeight: 120
};

const badge = {
  fontSize: 9,
  padding: '2px 6px',
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase'
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #10b981',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '50%',
      height: '100%',
      background: '#10b981',
      animation: 'bar 2s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(50%); }
        100% { transform: translateX(150%); }
      }
    `}</style>
  </div>
);

export default ExtractControlObjectivesButton;









// // src/components/RPA/buttons/ExtractControlObjectivesButton.js
// import React, { useEffect, useRef, useState } from 'react';
// import axios from 'axios';

// const api = process.env.REACT_APP_API;

// const POLL_MS = 5000;        // Poll every 5 seconds
// const MAX_MINUTES = 15;      // Stop polling after 15 minutes

// const ExtractControlObjectivesButton = ({ docId, disabledHint, onDone }) => {
//   const [busy, setBusy] = useState(false);
//   const [open, setOpen] = useState(false);

//   // analysis result and/or status
//   const [result, setResult] = useState(null);
//   const [err, setErr] = useState('');

//   // progress & status
//   const [phase, setPhase] = useState('idle');      // idle | queued | extracting | done | error
//   const [queuedMsg, setQueuedMsg] = useState('');
//   const [elapsedSec, setElapsedSec] = useState(0);
//   const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

//   const timerRef = useRef(null);
//   const tickRef = useRef(null);

//   // --- helpers --------------------------------------------------------------

//   const stopTimers = () => {
//     if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
//     if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
//   };

//   const closePanel = () => {
//     stopTimers();
//     setOpen(false);
//   };

//   // Poll /admin/rpa/docs for this docId, return doc or null
//   const fetchDocMeta = async () => {
//     const res = await axios.get(`${api}/admin/rpa/docs`, { withCredentials: true });
//     const docs = Array.isArray(res?.data?.items) ? res.data.items : [];
//     return docs.find(d => String(d._id) === String(docId)) || null;
//   };

//   // Polling loop when queued
//   const beginPolling = () => {
//     // elapsed timer
//     tickRef.current = setInterval(() => {
//       setElapsedSec(sec => sec + 1);
//     }, 1000);

//     // status poller
//     timerRef.current = setInterval(async () => {
//       try {
//         const d = await fetchDocMeta();
//         if (!d) return;

//         // If controlObjectives present, we are done
//         if (d.controlObjectives) {
//           stopTimers();
//           setPhase('done');
//           setResult({
//             success: true,
//             analysis: { 
//               azure: true, 
//               objectives: d.controlObjectives 
//             },
//           });
//           if (typeof onDone === 'function') onDone(); // refresh doc list
//           return;
//         }

//         // Still queued/working — update the UI hints
//         if (d.controlObjectivesQueuedAt) {
//           setPhase('extracting');
//           setLastUpdatedAt(d.updatedAt || d.createdAt || null);
//         }

//         // Safety stop after MAX_MINUTES
//         if (elapsedSec >= MAX_MINUTES * 60) {
//           stopTimers();
//           setPhase('error');
//           setErr('Control objectives extraction timed out. Please try again or check the SOC analysis results.');
//         }
//       } catch (e) {
//         // transient errors — keep polling unless it persists too long
//         console.warn('[RPA] control objectives status poll failed:', e?.message || e);
//       }
//     }, POLL_MS);
//   };

//   const run = async () => {
//     if (!docId) return;
//     setBusy(true);
//     setErr('');
//     setResult(null);
//     setElapsedSec(0);
//     setLastUpdatedAt(null);
//     stopTimers();

//     try {
//       // kick off control objectives extraction
//       const res = await axios.post(
//         `${api}/admin/rpa/docs/${docId}/extract/objectives`,
//         {},
//         { withCredentials: true, validateStatus: () => true }
//       );

//       // 200 => extraction is already available (cached) or completed inline
//       if (res.status === 200) {
//         setResult(res.data || { message: 'No data returned' });
//         setPhase('done');
//         setOpen(true);
//         if (typeof onDone === 'function') onDone(); // refresh doc list
//         return;
//       }

//       // 202 => queued; start polling
//       if (res.status === 202) {
//         setPhase('queued');
//         setQueuedMsg(res.data?.message || 'Queued for control objectives extraction...');
//         setOpen(true);
//         beginPolling();
//         return;
//       }

//       // other => error
//       console.error('[RPA] extract control objectives unexpected:', res.status, res.data);
//       setErr(res.data?.message || 'Control objectives extraction failed.');
//       setPhase('error');
//       setOpen(true);
//     } catch (e) {
//       console.error('[RPA] extract control objectives failed:', e);
//       setErr('Control objectives extraction failed.');
//       setPhase('error');
//       setOpen(true);
//     } finally {
//       setBusy(false);
//     }
//   };

//   // Clean up timers if component unmounts or panel closes
//   useEffect(() => {
//     return () => stopTimers();
//   }, []);

//   // --- render helpers -------------------------------------------------------

//   const renderSourceLabel = () => {
//     if (!result) return null;

//     // If Azure AI responded
//     if (result.analysis?.azure) {
//       return (
//         <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
//           ✅ AI-Powered Control Objectives Extraction
//         </p>
//       );
//     }

//     // Otherwise fallback heuristic
//     return (
//       <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
//         ⚠️ Heuristic Extraction (Fallback)
//       </p>
//     );
//   };

//   const renderControlObjectives = () => {
//     if (!result?.analysis?.objectives) return null;

//     let objectives;
//     try {
//       objectives = typeof result.analysis.objectives === 'string' 
//         ? JSON.parse(result.analysis.objectives) 
//         : result.analysis.objectives;
//     } catch {
//       return <p style={{ color: '#666' }}>Unable to parse control objectives</p>;
//     }

//     const controlObjs = objectives.control_objectives || [];
//     const summary = objectives.summary || {};

//     return (
//       <div style={objectivesContainer}>
//         {/* Summary Box */}
//         <div style={summaryBox}>
//           <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
//             Extraction Summary
//           </h4>
//           <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
//             <div><strong>Total Objectives:</strong> {summary.total_objectives || controlObjs.length}</div>
//             <div><strong>Critical Objectives:</strong> {summary.critical_objectives || 0}</div>
//             <div><strong>Testing Complexity:</strong> {summary.testing_complexity || 'Not specified'}</div>
//             <div><strong>Estimated Testing Hours:</strong> {summary.estimated_testing_hours || 'Not estimated'}</div>
//           </div>
//         </div>

//         {/* Control Objectives List */}
//         {controlObjs.length > 0 && (
//           <div style={{ marginTop: 16 }}>
//             <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
//               Control Objectives ({controlObjs.length})
//             </h4>
//             <div style={objectivesGrid}>
//               {controlObjs.slice(0, 6).map((obj, idx) => (
//                 <div key={idx} style={objectiveCard}>
//                   <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
//                     {obj.objective_id || `Objective ${idx + 1}`}
//                   </div>
//                   <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151', marginBottom: 4 }}>
//                     {obj.objective_title}
//                   </div>
//                   <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
//                     {obj.description}
//                   </div>
//                   <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
//                     <span style={{ ...badge, background: getRiskColor(obj.risk_level) }}>
//                       {obj.risk_level || 'medium'}
//                     </span>
//                     <span style={{ ...badge, background: '#f3f4f6', color: '#6b7280' }}>
//                       {obj.control_domain || 'general'}
//                     </span>
//                     <span style={{ ...badge, background: '#ecfdf5', color: '#065f46' }}>
//                       {obj.testing_approach || 'inquiry'}
//                     </span>
//                   </div>
//                 </div>
//               ))}
//               {controlObjs.length > 6 && (
//                 <div style={{ ...objectiveCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                   <span style={{ fontSize: 12, color: '#6b7280' }}>
//                     +{controlObjs.length - 6} more objectives...
//                   </span>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {controlObjs.length === 0 && (
//           <p style={{ color: '#6b7280', fontSize: 12 }}>
//             No control objectives were extracted. Please verify the SOC analysis results contain control information.
//           </p>
//         )}
//       </div>
//     );
//   };

//   const renderStatus = () => {
//     if (phase === 'queued') {
//       return (
//         <div style={statusBox}>
//           <strong>Queued for Extraction</strong>
//           <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
//           <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
//             <Spinner />
//             <span style={{ fontSize: 12, color: '#777' }}>
//               Processing SOC findings... {formatElapsed(elapsedSec)}
//             </span>
//           </div>
//         </div>
//       );
//     }

//     if (phase === 'extracting') {
//       return (
//         <div style={statusBox}>
//           <strong>Extracting Control Objectives...</strong>
//           <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
//             Analyzing SOC findings to identify testable control objectives
//           </div>
//           <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
//             <Spinner />
//             <span style={{ fontSize: 12, color: '#777' }}>
//               This typically takes 1-3 minutes. Elapsed {formatElapsed(elapsedSec)}
//               {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
//             </span>
//           </div>
//           <ProgressBar />
//         </div>
//       );
//     }

//     if (phase === 'error') {
//       return <p style={{ color: 'crimson' }}>{err || 'Something went wrong.'}</p>;
//     }

//     return null;
//   };

//   return (
//     <div>
//       <button
//         onClick={run}
//         disabled={busy || !!disabledHint}
//         title={disabledHint || 'Extract structured control objectives from SOC analysis'}
//         style={{ 
//           background: '#10b981', 
//           color: 'white', 
//           border: 'none', 
//           padding: '4px 8px', 
//           borderRadius: 4,
//           fontSize: 12,
//           cursor: disabledHint ? 'not-allowed' : 'pointer',
//           opacity: disabledHint ? 0.6 : 1
//         }}
//       >
//         {busy ? 'Extracting…' : 'Extract Objectives'}
//       </button>

//       {open && (
//         <div style={panel}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
//             <strong>Control Objectives Extraction</strong>
//             <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
//           </div>

//           {renderStatus()}

//           {/* Show the final result once ready */}
//           {phase === 'done' && !err && result && (
//             <>
//               {renderSourceLabel()}
//               {renderControlObjectives()}
              
//               {/* Raw JSON for debugging */}
//               <details style={{ marginTop: 12 }}>
//                 <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
//                   Raw Objectives (Debug)
//                 </summary>
//                 <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
//                   {typeof result.analysis.objectives === 'string' 
//                     ? result.analysis.objectives 
//                     : JSON.stringify(result.analysis.objectives, null, 2)}
//                 </pre>
//               </details>
//             </>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// /* ------------------------------- helpers & styles ---------------------------- */

// function getRiskColor(risk) {
//   switch (risk) {
//     case 'critical': return '#fef2f2';
//     case 'high': return '#fef3c7';
//     case 'medium': return '#eff6ff';
//     case 'low': return '#f0fdf4';
//     default: return '#f3f4f6';
//   }
// }

// function formatElapsed(sec) {
//   const m = Math.floor(sec / 60);
//   const s = sec % 60;
//   return `${m}:${String(s).padStart(2, '0')}`;
// }

// const panel = {
//   marginTop: 8,
//   background: '#f8f9fb',
//   border: '1px solid #e2e5ea',
//   padding: 12,
//   borderRadius: 8,
//   maxWidth: 800,
//   boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
// };

// const statusBox = {
//   background: '#fff',
//   border: '1px solid #e6e9ef',
//   padding: 10,
//   borderRadius: 8,
//   marginBottom: 10
// };

// const objectivesContainer = {
//   background: '#fff',
//   border: '1px solid #e6e9ef',
//   padding: 12,
//   borderRadius: 8,
//   marginBottom: 8
// };

// const summaryBox = {
//   background: '#f0f9ff',
//   border: '1px solid #0ea5e9',
//   padding: 10,
//   borderRadius: 6
// };

// const objectivesGrid = {
//   display: 'grid',
//   gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
//   gap: 8
// };

// const objectiveCard = {
//   background: '#f9fafb',
//   border: '1px solid #e5e7eb',
//   padding: 10,
//   borderRadius: 6,
//   minHeight: 120
// };

// const badge = {
//   fontSize: 9,
//   padding: '2px 6px',
//   borderRadius: 12,
//   fontWeight: 'bold',
//   textTransform: 'uppercase'
// };

// const Spinner = () => (
//   <span
//     style={{
//       width: 14,
//       height: 14,
//       border: '2px solid #cbd3e1',
//       borderTop: '2px solid #10b981',
//       borderRadius: '50%',
//       display: 'inline-block',
//       animation: 'spin 1s linear infinite'
//     }}
//   />
// );

// const ProgressBar = () => (
//   <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
//     <div style={{
//       width: '50%',
//       height: '100%',
//       background: '#10b981',
//       animation: 'bar 2s ease-in-out infinite'
//     }} />
//     <style>{`
//       @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
//       @keyframes bar {
//         0% { transform: translateX(-100%); }
//         50% { transform: translateX(50%); }
//         100% { transform: translateX(150%); }
//       }
//     `}</style>
//   </div>
// );

// export default ExtractControlObjectivesButton;